import {Avatar} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useParams} from '@tanstack/react-router'
import React from "react";
import img_male from "../../../assets/img_male.png";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PTable from "../../../components/PTable.tsx";
import {CoinsRewardText} from "../../../constants.ts";
import Header from "../../../Header.tsx";
import {useJwtAuth} from "../../../JWTAuthProvider.tsx";
import {tableFullScreen} from "../../../components/tableStyles.ts";
import services from "../../../services";
import {CoinsRedemption} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";
import useIsSm from "../../../utilities/useIsSm.ts";

export const Route = createFileRoute('/_app/master/coins-redemptions')({
    component: RouteComponent,
})

function RouteComponent() {
    const jwt = useJwtAuth();
    const user = jwt.user;
    const params = useParams({strict: false});
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const redemptionsQuery = useQuery<CoinsRedemption[]>({
        queryKey: queryKeys.coins_redemptions(),
        queryFn:  services.coins.redemptions,
    })

    const redemptionsUpdate = useMutation({
        mutationFn: services.coins.updateRedemption,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.coins_redemptions()})
        }
    })

    const isSm = useIsSm();

    const columns = (isSm
            ? [
                {key: 'combined', title: 'User'},
                {key: 'actions', title: ''},
            ]
            : [
                {key: 'name', title: 'User'},
                {key: 'reward', title: 'Reward'},
                {key: 'fulfilled', title: 'Date Fulfilled'},
                {key: 'actions', title: ''},
            ]
    );

    if (!user) {
        return <div>
            <Header />

            <Content>Please login.</Content>
        </div>;
    }

    if (redemptionsQuery.isLoading) {
        return <div className="flex justify-center my-6">
            <Spinner size={"lg"} />
        </div>
    }

    return (
        <>
            <Header backUrl={params.player_uid ? '/players' : undefined} />

            <Content>
                <PTable removeWrapper={false} classNames={tableFullScreen} isStriped={true} aria-label={"Club Players"}>
                    <TableHeader columns={columns}>
                        {(column) => <TableColumn key={column.key}>{column.title}</TableColumn>}
                    </TableHeader>
                    <TableBody>
                        {(redemptionsQuery.data || []).map((redemption: CoinsRedemption) => <TableRow key={redemption.uid}>
                            {(columnKey) => {
                                if (columnKey === 'combined') {
                                    return <TableCell>
                                        <div className="flex w-full">
                                            <div>
                                                <Avatar className={"mr-2"} src={redemption.user?.profile_thumb_url || redemption.user?.profile_image_url || img_male} />
                                            </div>
                                            <span className="flex-1">
                                                {redemption.user?.name} {redemption.user?.lastname}<br />
                                                <small>
                                                    {CoinsRewardText(redemption.coins_reward)}
                                                    {redemption.coins_reward?.includes('amazon') &&
                                                        <div>Region: {redemption.meta?.amazon_region}</div>
                                                    }
                                                </small>
                                                {!redemption.fulfilled_at && <small>
                                                    Not fulfilled
                                                </small>}
                                            </span>
                                        </div>
                                    </TableCell>
                                }
                                if (columnKey === 'name') {
                                    return <TableCell>
                                        <div className={"flex w-full items-center"}>
                                            <Avatar className={"mr-2"} src={redemption.user?.profile_thumb_url || redemption.user?.profile_image_url || img_male} />
                                            <div>
                                                {redemption.user?.name} {redemption.user?.lastname}<br />
                                            </div>
                                        </div>
                                    </TableCell>
                                }
                                if (columnKey === 'reward') {
                                    return <TableCell>
                                        {CoinsRewardText(redemption.coins_reward)}
                                        {redemption.coins_reward?.includes('amazon') &&
                                            <div>Region: {redemption.meta?.amazon_region}</div>
                                        }
                                    </TableCell>
                                }
                                if (columnKey === 'fulfilled') {
                                    return <TableCell>
                                        {redemption.fulfilled_at?.replace(/ .*/, '')}
                                    </TableCell>
                                }
                                if (columnKey === 'actions') {
                                    return <TableCell className={"flex justify-end gap-2"}>
                                        {!redemption.fulfilled_at &&
                                            <PButton color={"primary"}
                                                     onPress={() => redemptionsUpdate.mutate({uid: redemption.uid, version: redemption.version + 1, fulfilled: true})}
                                            >
                                                Mark as fulfilled
                                            </PButton>
                                        }

                                        <PButton className="mr-2" isIconOnly onPress={() => navigate({to: `/profiles/${redemption.user?.uid}`})}><Icon icon="material-symbols-light:open-in-new" className="text-2xl" /></PButton>
                                    </TableCell>
                                }
                                if (redemption.user?.hasOwnProperty(columnKey)) {
                                    return <TableCell>
                                        {redemption.user?.[columnKey] || '-'}
                                    </TableCell>
                                }
                                return (
                                    <TableCell>
                                        {columnKey}
                                    </TableCell>
                                );
                            }}
                        </TableRow>)}
                    </TableBody>

                </PTable>

            </Content>
        </>
    );
}